import {
  browserSessionPersistence,
  GoogleAuthProvider,
  User,
  onAuthStateChanged,
  setPersistence,
  signInAnonymously,
  signInWithPopup,
  signOut,
} from 'firebase/auth';

import { getFirebaseAuth } from './client';

let sessionPersistencePromise: Promise<void> | null = null;
const LOCALHOST_AUTH_HOSTS = new Set(['localhost', '127.0.0.1']);

export interface AuthClaims {
  approved: boolean;
  admin: boolean;
}

const FALLBACK_AUTH_CLAIMS: AuthClaims = {
  approved: true,
  admin: false,
};
const INVALID_EMULATOR_SESSION_CODES = new Set([
  'auth/invalid-user-token',
  'auth/network-request-failed',
  'auth/user-token-expired',
]);

const isEmulatorMode = (): boolean => import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true';
const toAuthErrorCode = (error: unknown): string =>
  error && typeof error === 'object' && typeof (error as { code?: unknown }).code === 'string'
    ? (error as { code: string }).code
    : '';
const toAuthErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message.trim() : String(error ?? '').trim();

const ensureSessionAuthPersistence = async (): Promise<void> => {
  if (!sessionPersistencePromise) {
    const auth = getFirebaseAuth();
    sessionPersistencePromise = setPersistence(auth, browserSessionPersistence).catch(
      (error: unknown) => {
        sessionPersistencePromise = null;
        throw error;
      },
    );
  }

  await sessionPersistencePromise;
};

export const observeAuthState = (callback: (user: User | null) => void): (() => void) => {
  const auth = getFirebaseAuth();
  void ensureSessionAuthPersistence().catch((error: unknown) => {
    console.warn('[auth] Failed to enforce session-only persistence', error);
  });
  return onAuthStateChanged(auth, callback);
};

export const isLocalhostAuthHost = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return LOCALHOST_AUTH_HOSTS.has(window.location.hostname);
};

const resolveAuthClaims = async (
  user: Pick<User, 'isAnonymous'> & {
    getIdTokenResult?: (forceRefresh?: boolean) => Promise<{ claims: Record<string, unknown> }>;
  },
  forceRefresh: boolean,
): Promise<AuthClaims> => {
  if (typeof user.getIdTokenResult !== 'function') {
    return FALLBACK_AUTH_CLAIMS;
  }

  const tokenResult = await user.getIdTokenResult(forceRefresh);
  if (user.isAnonymous && isLocalhostAuthHost()) {
    const approvedClaim = tokenResult.claims.approved;
    return {
      approved: approvedClaim === false ? false : true,
      admin: false,
    };
  }

  return {
    approved: tokenResult.claims.approved === true,
    admin: tokenResult.claims.admin === true,
  };
};

export const getAuthClaims = async (
  user: Pick<User, 'isAnonymous'> & {
    getIdTokenResult?: (forceRefresh?: boolean) => Promise<{ claims: Record<string, unknown> }>;
  },
): Promise<AuthClaims> => resolveAuthClaims(user, false);

export const refreshAuthClaims = async (
  user: Pick<User, 'isAnonymous'> & {
    getIdTokenResult?: (forceRefresh?: boolean) => Promise<{ claims: Record<string, unknown> }>;
  },
): Promise<AuthClaims> => resolveAuthClaims(user, true);

export const recoverInvalidEmulatorSession = async (error: unknown): Promise<boolean> => {
  if (!isEmulatorMode()) {
    return false;
  }

  const code = toAuthErrorCode(error);
  const message = toAuthErrorMessage(error);
  if (
    !INVALID_EMULATOR_SESSION_CODES.has(code) &&
    !/securetoken|invalid user token|user token expired/i.test(message)
  ) {
    return false;
  }

  try {
    await signOut(getFirebaseAuth());
  } catch (signOutError) {
    console.warn('[auth] Failed to clear invalid emulator session', signOutError);
  }

  return true;
};

export const signInWithGoogle = async (): Promise<void> => {
  await ensureSessionAuthPersistence();
  const auth = getFirebaseAuth();
  await signInWithPopup(auth, new GoogleAuthProvider());
};

export const signInAsAnonymous = async (): Promise<void> => {
  await ensureSessionAuthPersistence();
  const auth = getFirebaseAuth();
  await signInAnonymously(auth);
};

export const signOutUser = async (): Promise<void> => {
  await signOut(getFirebaseAuth());
};
